// Slide-in receipt preview drawer. After the patient downloads a session
// receipt PDF we also keep the blob URL alive and render it in this drawer:
//   - desktop (≥ 768px): slides in from the right edge, takes ~55vw
//   - mobile (< 768px): slides up from the bottom, takes ~85vh
//
// The actual file download still happens in the browser (handled in
// sessionsService.downloadReceiptPdf) — this drawer is purely the preview.
// On close we revoke the blob URL so the browser can free memory.

import React, { useEffect } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { GhostButton, PrimaryButton } from '../layout/styledComponents'

const slideInRight = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`
const slideInUp = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(31, 41, 55, 0.45);
  z-index: ${(p) => (p.theme?.z?.modal || 60) - 1};
  animation: ${fadeIn} 220ms ease-out;
`

const Panel = styled.section`
  position: fixed;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  z-index: ${(p) => p.theme?.z?.modal || 60};
  display: flex;
  flex-direction: column;
  /* Mobile-first — bottom-up sheet */
  left: 0;
  right: 0;
  bottom: 0;
  height: 85vh;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 -12px 40px rgba(31, 41, 55, 0.18);
  animation: ${slideInUp} 280ms cubic-bezier(0.16, 1, 0.3, 1);
  @media (min-width: 768px) {
    /* Desktop — right-side panel */
    top: 0;
    right: 0;
    left: auto;
    bottom: 0;
    height: 100%;
    width: min(640px, 60vw);
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
    border-top-right-radius: 0;
    box-shadow: -12px 0 40px rgba(31, 41, 55, 0.18);
    animation: ${slideInRight} 280ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  flex-shrink: 0;
`

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleSub = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
`

const CloseButton = styled.button`
  appearance: none;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  &:hover { background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'}; }
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  background: ${(p) => p.theme?.color?.bg || '#FAF7F2'};
  ${(p) =>
    p.$loading &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
    `}
`

const Frame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  background: ${(p) => p.theme?.color?.bg || '#FAF7F2'};
`

const TextFallback = styled.pre`
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  padding: 24px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
`

const Footer = styled.footer`
  border-top: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} [props.title]    e.g. "Receipt"
 * @param {string} [props.subtitle] e.g. "Therapy session · 5 May 2026"
 * @param {string} [props.blobUrl]  blob: URL of the PDF (or text in mock mode)
 * @param {string} [props.mimeType] 'application/pdf' | 'text/plain' | …
 * @param {string} [props.filename] for the "downloaded as …" footer line
 * @param {string} [props.fallbackText] for non-PDF mock blobs (text fallback)
 * @param {Function} props.onClose called when user dismisses the drawer
 * @param {Function} [props.onDownloadAgain] re-trigger save (e.g. user closed
 *   the dialog and re-opened, or first save was blocked)
 */
export default function ReceiptDrawer({
  open,
  title = 'Receipt',
  subtitle,
  blobUrl,
  mimeType,
  filename,
  fallbackText,
  onClose,
  onDownloadAgain,
}) {
  // Esc key dismissal — single source of truth for keyboard close.
  useEffect(() => {
    if (!open) return undefined
    const handler = (e) => {
      if (e.key === 'Escape') onClose && onClose()
    }
    window.addEventListener('keydown', handler)
    // Lock body scroll while open so the underlying page doesn't scroll
    // through the drawer on iOS Safari.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handler)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  const isPdf = mimeType === 'application/pdf' || (mimeType || '').toLowerCase().includes('pdf')

  return (
    <>
      <Backdrop onClick={onClose} aria-hidden />
      <Panel role="dialog" aria-label={title}>
        <Header>
          <Title>
            {title}
            {subtitle ? <TitleSub>{subtitle}</TitleSub> : null}
          </Title>
          <HeaderActions>
            {onDownloadAgain ? (
              <PrimaryButton
                onClick={onDownloadAgain}
                style={{ padding: '8px 14px', fontSize: 13 }}
              >
                Download
              </PrimaryButton>
            ) : null}
            <CloseButton aria-label="Close" onClick={onClose}>
              ✕
            </CloseButton>
          </HeaderActions>
        </Header>
        <Body>
          {blobUrl && isPdf ? (
            <Frame src={blobUrl} title={title} />
          ) : blobUrl ? (
            // Mock-mode text fallback — the dev blob is a tiny note rather
            // than a real PDF. Render it inline so the UX is still clear.
            <TextFallback>{fallbackText || 'Receipt content not available.'}</TextFallback>
          ) : (
            <TextFallback>Loading…</TextFallback>
          )}
        </Body>
        <Footer>
          <span>{filename ? `Saved as ${filename}` : 'Receipt preview'}</span>
          <GhostButton onClick={onClose} style={{ padding: '6px 14px', fontSize: 13 }}>
            Close
          </GhostButton>
        </Footer>
      </Panel>
    </>
  )
}
